import { Container, Eyebrow } from "@constructorfleet/ui/design-system";
import { Link } from "react-router-dom";

export function TermsPage() {
  return (
    <div className="policy-page">
      <section className="ph">
        <Container>
          <Eyebrow brand dot>
            Legal
          </Eyebrow>
          <h1>Terms &amp; Conditions</h1>
          <p className="lede">Last updated March 23, 2026</p>
        </Container>
      </section>

      <section className="policy-body">
        <Container small>
          <section className="policy-section">
            <h2>About ConstructorFleet</h2>
            <p>
              These Terms &amp; Conditions are between you and ConstructorFleet L.L.C., the operator
              of the ConstructorFleet platform. References to &ldquo;ConstructorFleet,&rdquo;
              &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo; in these terms refer to
              ConstructorFleet L.L.C.
            </p>
          </section>

          <section className="policy-section">
            <h2>Acceptance of Terms</h2>
            <p>
              By creating an account, purchasing a subscription, or using any ConstructorFleet
              service, you agree to these Terms &amp; Conditions. If you do not agree, do not use
              the service.
            </p>
          </section>

          <section className="policy-section">
            <h2>Eligibility</h2>
            <p>
              ConstructorFleet services are intended only for users in the United States. You must
              be legally able to enter into this agreement and use the service only for lawful
              purposes.
            </p>
          </section>

          <section className="policy-section">
            <h2>Nature of the Service</h2>
            <p>
              ConstructorFleet products are software tools that automate specific tasks on your
              behalf, including voice, messaging, transcript analysis, and workflow automation. The
              outcome of any automated action depends on factors outside our control, including
              third-party carriers, external systems, network conditions, and the accuracy of
              third-party IVR or voice interactions.
            </p>
            <p>
              ConstructorFleet operates solely as a tool for individual users and does not act on
              behalf of any clinic, provider, or treatment program. ConstructorFleet does not
              provide medical, clinical, or healthcare services. All data processed by
              ConstructorFleet is collected at the direction of the user and is not received from or
              maintained on behalf of any healthcare provider. ConstructorFleet is not affiliated
              with or endorsed by any clinic, provider, or treatment program, including BHG.
            </p>
          </section>

          <section className="policy-section">
            <h2>Account Responsibilities</h2>
            <p>
              You are responsible for maintaining accurate account information, securing your
              credentials, and restricting access to your account and API keys. You must not share
              your account with others or use the service to violate law, third-party rights, or
              program requirements.
            </p>
          </section>

          <section className="policy-section">
            <h2>Subscriptions, Billing, and Refunds</h2>
            <p>
              Paid services are billed on a recurring basis until cancelled. By subscribing, you
              authorize ConstructorFleet and its payment processor to charge the applicable
              subscription fees, taxes, and any valid add-on charges using your selected payment
              method.
            </p>
            <p>
              You may cancel at any time. Upon cancellation, your subscription remains active
              through the end of the current billing period and will not renew. If you request
              immediate cancellation, we will cancel your subscription right away and issue a
              prorated refund for the unused portion of the current billing period. Additional
              details are described in our <Link to="/refunds">Refund Policy</Link>.
            </p>
          </section>

          <section className="policy-section">
            <h2>Communications Consent</h2>
            <p>
              By providing your mobile phone number and enabling SMS notifications in your account
              settings, you consent to receive communications from ConstructorFleet related to your
              account and service use.
            </p>
            <p>
              These messages may include password reset codes, account security alerts, operational
              notices, and, when enabled for a product like BHG Check-in Assist, BHG Check-in Assist
              schedule updates and related status notifications.
            </p>
            <p>
              Consent is not required to use ConstructorFleet services. Message frequency varies
              based on your account activity and enabled features. Standard Message and Data Rates
              may apply. You can opt out of SMS by replying STOP, reply HELP for help, or manage
              preferences in your settings page. We will not share mobile information with third
              parties for promotional or marketing purposes. Carrier delivery is not guaranteed, and
              you are responsible for providing accurate contact details and updating them if they
              change.
            </p>
            <p>
              Additional details are available in our <Link to="/privacy">Privacy Policy</Link> and
              these <Link to="/terms">Terms &amp; Conditions</Link>.
            </p>
          </section>

          <section className="policy-section">
            <h2>API Access &amp; Webhooks</h2>
            <p>
              The API Access add-on provides programmatic access to your call data through a REST
              API and real-time webhook notifications. Use of API Access is subject to the following
              terms.
            </p>
            <h3>Rate Limits</h3>
            <p>
              API requests are rate-limited to ensure fair usage and service stability. The current
              default limit is 60 requests per minute per API key. Requests that exceed the rate
              limit will receive a 429 (Too Many Requests) response. Rate limits may be adjusted at
              any time without prior notice.
            </p>
            <h3>API Keys</h3>
            <p>
              API keys authenticate requests on your behalf and must be kept confidential. You are
              responsible for all activity performed using your API keys. Revoke any key you believe
              has been compromised immediately from your dashboard.
            </p>
            <h3>Webhook Delivery</h3>
            <p>
              Webhook notifications are delivered on a best-effort basis. We do not guarantee
              delivery order, exactly-once delivery, or delivery within a specific time window. Your
              endpoint must respond with a 2xx status code within 10 seconds; otherwise the delivery
              attempt is considered failed. Failed deliveries may be retried up to 3 times with
              exponential backoff.
            </p>
            <h3>Webhook Signing Secrets</h3>
            <p>
              You may configure a signing secret to verify that webhook payloads originate from
              ConstructorFleet. It is your responsibility to validate signatures on incoming
              requests. We are not liable for actions taken on unsigned or unverified payloads.
            </p>
            <h3>Data &amp; Retention</h3>
            <p>
              API responses contain the same call data visible on your dashboard. Data retention
              policies apply equally whether data is accessed through the dashboard or the API. Do
              not redistribute, resell, or make API data available to unauthorized third parties.
            </p>
            <h3>Acceptable Use</h3>
            <p>
              You agree not to use the API to exceed rate limits through circumvention, reverse
              engineer or probe the service infrastructure, store or transmit data in violation of
              applicable law, or interfere with other users&apos; access to the service. Violation
              of these terms may result in immediate suspension of API access.
            </p>
          </section>

          <section className="policy-section">
            <h2>Service Availability</h2>
            <p>
              We make commercially reasonable efforts to keep services available and reliable.
              However, we do not guarantee uninterrupted availability. Scheduled maintenance,
              third-party outages, or unforeseen technical issues may cause temporary disruptions.
            </p>
          </section>

          <section className="policy-section">
            <h2>Warranty Disclaimer &amp; Limitation of Liability</h2>
            <p>
              ConstructorFleet services are provided on an &quot;as is&quot; and &quot;as
              available&quot; basis. To the fullest extent permitted by law, we disclaim all
              warranties, express or implied, including warranties of merchantability, fitness for a
              particular purpose, non-infringement, and uninterrupted availability.
            </p>
            <p>
              We are not responsible, and will not be held responsible, for any consequences that
              arise if a product fails to operate as expected. This includes but is not limited to
              missed check-ins, loss of program standing, financial loss, disciplinary action, or
              any other outcome resulting from a service failure or interruption. You are solely
              responsible for ensuring your obligations are met and must not rely exclusively on
              automated services, including ours, to satisfy obligations that carry serious personal
              or legal consequences.
            </p>
          </section>

          <section className="policy-section">
            <h2>Suspension &amp; Termination</h2>
            <p>
              We may suspend or terminate access to the service if you violate these terms, fail to
              pay applicable fees, create security or legal risk, or misuse the service. You may
              stop using the service at any time.
            </p>
          </section>

          <section className="policy-section">
            <h2>Dispute Resolution</h2>
            <p>
              Any dispute, claim, or controversy arising out of or relating to these terms or the
              service will be resolved by binding arbitration on an individual basis, except where
              applicable law requires otherwise or permits small-claims court. By agreeing to these
              terms, you waive any right to participate in a class action to the fullest extent
              permitted by law.
            </p>
          </section>

          <section className="policy-section">
            <h2>Governing Law</h2>
            <p>
              These terms are governed by the Federal Arbitration Act and the laws of the state in
              which ConstructorFleet is organized, without regard to conflict-of-law rules.
            </p>
          </section>

          <section className="policy-section">
            <h2>Modifications</h2>
            <p>
              We may update these terms from time to time. Continued use of the service after
              changes are posted constitutes acceptance of the updated terms.
            </p>
          </section>
        </Container>
      </section>
    </div>
  );
}
